import { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { Btn, Field } from "./ui";
import { toast } from "sonner";
import { Sparkles, Check, X, RefreshCw, Loader2 } from "lucide-react";

const err = (e) => toast.error(e?.response?.data?.detail || "Erro");

function DraftEditor({ pid, draft, onDone }) {
  const [d, setD] = useState({ title: draft.title_pt, description: draft.description_pt, title_en: draft.title_en, description_en: draft.description_en });
  const [busy, setBusy] = useState(false);
  const bind = (k, rows) => {
    const props = { className: "input-almar", value: d[k] || "", "data-testid": `ai-draft-${k}`, onChange: (e) => setD({ ...d, [k]: e.target.value }) };
    return rows ? <textarea rows={rows} {...props} /> : <input {...props} />;
  };
  const approve = async () => {
    setBusy(true);
    try { await api.post(`/re/properties/${pid}/ai-text/approve`, d); toast.success("Texto aprovado e aplicado ao imóvel"); onDone(d); }
    catch (e) { err(e); }
    setBusy(false);
  };
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="space-y-3"><p className="text-xs uppercase tracking-wider text-[#C9A84C]">Português</p>
          <Field label="Título">{bind("title")}</Field><Field label="Descrição">{bind("description", 9)}</Field></div>
        <div className="space-y-3"><p className="text-xs uppercase tracking-wider text-[#C9A84C]">English</p>
          <Field label="Title">{bind("title_en")}</Field><Field label="Description">{bind("description_en", 9)}</Field></div>
      </div>
      <div className="flex flex-wrap gap-2 justify-end">
        <Btn onClick={approve} disabled={busy} data-testid="ai-approve-btn"><Check size={15} /> Aprovar e aplicar</Btn>
      </div>
    </div>
  );
}

export function AiTextPanel({ property, setP }) {
  const pid = property.id;
  const draft = property.ai_draft;
  const status = draft?.status;
  const stale = status === "generating" && Date.now() - new Date(draft.generated_at).getTime() > 180000;
  const refresh = useCallback(() => api.get(`/re/properties/${pid}`).then((r) => setP((prev) => ({ ...prev, ai_draft: r.data.ai_draft }))).catch(() => {}), [pid, setP]);
  useEffect(() => {
    if (status !== "generating" || stale) return undefined;
    const t = setInterval(refresh, 4000);
    return () => clearInterval(t);
  }, [status, stale, refresh]);
  const gen = () => api.post(`/re/properties/${pid}/ai-text`).then(refresh).catch(err);
  const discard = () => api.delete(`/re/properties/${pid}/ai-text`).then(() => { toast.success("Rascunho descartado"); setP((prev) => ({ ...prev, ai_draft: undefined })); }).catch(err);
  const applied = (d) => setP((prev) => ({ ...prev, ...d, ai_draft: undefined }));

  return (
    <div className="card-almar p-4 mb-4 border border-[#C9A84C]/30" data-testid="ai-text-panel">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-slate-100"><Sparkles size={17} className="text-[#E0C574]" />
          <span className="font-medium">Texto comercial com IA</span>
          {status === "ready" && <span className="text-xs text-[#E0C574]" data-testid="ai-status">Rascunho para rever · {draft.photos_used} fotografia(s) analisada(s)</span>}
          {status === "generating" && <span className="text-xs text-slate-400 flex items-center gap-1" data-testid="ai-status"><Loader2 size={13} className="animate-spin" /> A gerar título e descrição...</span>}
          {status === "error" && <span className="text-xs text-red-300" data-testid="ai-status">{draft.error}</span>}
        </div>
        <div className="flex gap-2">
          {(status !== "generating" || stale) && <Btn variant="ghost" onClick={gen} data-testid="ai-generate-btn">{status ? <RefreshCw size={15} /> : <Sparkles size={15} />} {status ? "Gerar novamente" : "Gerar texto"}</Btn>}
          {status && (status !== "generating" || stale) && <Btn variant="ghost" onClick={discard} data-testid="ai-discard-btn"><X size={15} /> Descartar</Btn>}
        </div>
      </div>
      {!status && <p className="text-xs text-slate-500 mt-2">Gera título e descrição em português e inglês a partir das características, localização e fotografias. Nada é aplicado sem a sua aprovação.</p>}
      {status === "ready" && <div className="mt-4"><DraftEditor key={draft.generated_at} pid={property.id} draft={draft} onDone={applied} /></div>}
    </div>
  );
}

export function AiBatchButton({ onQueued }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!window.confirm("Gerar título e descrição com IA para todos os imóveis \"Em preparação\" sem rascunho? Os textos ficam como rascunho para aprovar na ficha de cada imóvel.")) return;
    setBusy(true);
    try {
      const r = await api.post("/re/ai-text/batch");
      toast.success(r.data.queued ? `${r.data.queued} imóvel(is) em geração. Os rascunhos aparecem na ficha de cada imóvel.` : "Não há imóveis \"Em preparação\" por gerar");
      onQueued?.();
    } catch (e) { err(e); }
    setBusy(false);
  };
  return <Btn variant="ghost" onClick={run} disabled={busy} data-testid="ai-batch-btn"><Sparkles size={15} /> Gerar textos (Em preparação)</Btn>;
}
